import { useState } from 'react'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import type { Resultats, Collaborateur, Parametres } from '../types'
import { perfCollab, formatEuros, formatEurosCents } from '../calculs'

interface Props {
  resultats: Resultats; collaborateurs: Collaborateur[]; parametres: Parametres
  caReel: number; onCaReelChange:(v:number)=>void
  onModeGerantChange: (mode: 'actif' | 'passif') => void
}
const COLORS = ['#1A3C6E','#2563EB','#3B82F6','#60A5FA','#93C5FD','#C62828','#BFDBFE','#DBEAFE']

export default function ResultatsSeuil({ resultats:r, collaborateurs, parametres:p, caReel, onCaReelChange, onModeGerantChange }:Props) {
  const [showCharts, setShowCharts] = useState(false)
  const ecartColor = r.ecartPct>20?'#C62828':r.ecartPct>10?'#E65100':'#2E7D32'
  const hasH       = r.heuresProductivesTotales > 0
  const hasCA      = collaborateurs.some(c=>(c.caReel??0)>0)
  const couverture = r.caMinimumTTC>0?(caReel/r.caMinimumTTC)*100:0

  const pieData = [
    {name:'Charges fixes',    value:Math.round(r.chargesFixesMensuelles),   color:'#1A3C6E'},
    {name:'Masse salariale',  value:Math.round(r.masseSalarialeMensuelle),  color:'#3B82F6'},
    {name:'Produits tech.',   value:Math.round(r.coutProduitsTechMensuel),  color:'#C62828'},
    {name:'Objectif bénéf.',  value:Math.round(p.objectifBenefice),         color:'#2E7D32'},
  ].filter(d=>d.value>0)

  const barData = Object.entries(r.chargesParCategorie).filter(([,v])=>v>0)
    .sort(([,a],[,b])=>b-a).map(([n,v])=>({name:n.slice(0,5),fullName:n,value:Math.round(v)}))

  return (
    <div className="space-y-3">

      {/* ── Seuil principal — bouton mode gérant en haut à droite ── */}
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        {/* Toggle gérant — en-tête */}
        <div className="flex items-center justify-between px-4 pt-3 pb-0">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Seuil de rentabilité</span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400">Gérant :</span>
            <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
              <button onClick={() => onModeGerantChange('actif')}
                className="px-2.5 py-1 font-medium transition-colors"
                style={{ background: p.modeGerant !== 'passif' ? '#1A3C6E' : 'white', color: p.modeGerant !== 'passif' ? 'white' : '#6B7280' }}
                title="Le gérant génère du CA et participe au seuil global">
                Actif ✍️
              </button>
              <button onClick={() => onModeGerantChange('passif')}
                className="px-2.5 py-1 font-medium transition-colors"
                style={{ background: p.modeGerant === 'passif' ? '#1A3C6E' : 'white', color: p.modeGerant === 'passif' ? 'white' : '#6B7280' }}
                title="L'équipe couvre les charges + salaire gérant — le gérant ne fait pas de CA">
                Passif 👑
              </button>
            </div>
          </div>
        </div>

        {/* Explication du mode */}
        <div className="mx-4 mt-2 px-3 py-2 rounded-xl text-xs"
          style={{ background: p.modeGerant === 'passif' ? '#FFF7ED' : '#F0F7FF', color: p.modeGerant === 'passif' ? '#92400E' : '#1A3C6E' }}>
          {p.modeGerant === 'passif'
            ? '👑 Gérant passif : son salaire est pris en charge par l\'équipe. Le seuil ci-dessous est celui que l\'équipe seule doit couvrir (sans le CA du gérant).'
            : '✍️ Gérant actif : il génère du CA et participe à couvrir les charges. Le seuil inclut sa contribution.'}
        </div>

        <div className="p-4" style={{background:'#1A3C6E'}}>
          {/* Valeur principale selon le mode */}
          {p.modeGerant === 'passif' ? (
            <div className="text-center">
              <div className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-1">
                Seuil équipe TTC (sans gérant)
              </div>
              <div className="flex items-end justify-center gap-6">
                <div>
                  <div className="text-3xl font-black text-white font-mono">{formatEuros(r.caMinimumSansGerantTTC)}</div>
                  <div className="text-white/50 text-xs mt-0.5 uppercase tracking-wide">/ mois</div>
                </div>
                <div className="text-white/20 text-xl pb-1">|</div>
                <div>
                  <div className="text-2xl font-black font-mono" style={{color:'rgba(255,255,255,0.75)'}}>{formatEuros(r.caMinimumSansGerantTTC*12)}</div>
                  <div className="text-white/40 text-xs mt-0.5 uppercase tracking-wide">/ an</div>
                </div>
              </div>
              <div className="text-white/50 text-xs mt-2">{formatEuros(r.seuilQuotidienSansGerantTTC)} / jour · {formatEurosCents(r.seuilMinuteSansGerantTTC)} / min</div>
              {/* Comparaison avec seuil complet */}
              <div className="mt-2 px-3 py-1.5 rounded-lg text-xs text-center" style={{background:'rgba(255,255,255,0.1)'}}>
                Seuil global (gérant inclus) : <strong className="text-white/80">{formatEuros(r.caMinimumTTC)}/mois</strong>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-1">
                CA Minimum TTC — à encaisser
              </div>
              <div className="flex items-end justify-center gap-6">
                <div>
                  <div className="text-3xl font-black text-white font-mono">{formatEuros(r.caMinimumTTC)}</div>
                  <div className="text-white/50 text-xs mt-0.5 uppercase tracking-wide">/ mois</div>
                </div>
                <div className="text-white/20 text-xl pb-1">|</div>
                <div>
                  <div className="text-2xl font-black font-mono" style={{color:'rgba(255,255,255,0.75)'}}>{formatEuros(r.caMinimumTTC*12)}</div>
                  <div className="text-white/40 text-xs mt-0.5 uppercase tracking-wide">/ an</div>
                </div>
              </div>
              <div className="text-white/50 text-xs mt-2">{formatEuros(r.seuilQuotidienTTC)} / jour TTC</div>
            </div>
          )}
          {/* HT */}
          <div className="mt-3 flex justify-center">
            <div className="px-4 py-2 rounded-xl text-center" style={{background:'rgba(255,255,255,0.12)'}}>
              <div className="text-xs text-white/50 mb-0.5">Seuil HT (avant TVA {p.tauxTVA}%)</div>
              <div className="flex items-center gap-3">
                {p.modeGerant === 'passif'
                  ? <><span className="font-bold font-mono text-white/80">{formatEuros(r.caMinimumSansGerantTTC/(1+p.tauxTVA/100))}<span className="text-xs font-normal text-white/50">/mois</span></span></>
                  : <><span className="font-bold font-mono text-white/80">{formatEuros(r.caMinimumHT)}<span className="text-xs font-normal text-white/50">/mois</span></span><span className="text-white/30">·</span><span className="font-bold font-mono text-white/60">{formatEuros(r.caMinimumHT*12)}<span className="text-xs font-normal text-white/40">/an</span></span></>
                }
              </div>
            </div>
          </div>
        </div>
        {/* Métriques */}
        <div className="grid grid-cols-2 divide-x divide-gray-100">
          <div className="p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Charges fixes HT</div>
            <div className="font-bold text-sm font-mono" style={{color:'#1A3C6E'}}>{formatEuros(r.chargesFixesMensuelles)}<span className="text-xs font-normal text-gray-400">/mois</span></div>
            <div className="text-xs text-gray-400">{formatEuros(r.chargesFixesMensuelles*12)}/an</div>
          </div>
          <div className="p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Masse salariale HT</div>
            <div className="font-bold text-sm font-mono" style={{color:'#1A3C6E'}}>{formatEuros(r.masseSalarialeMensuelle)}<span className="text-xs font-normal text-gray-400">/mois</span></div>
            <div className="text-xs text-gray-400">{formatEuros(r.masseSalarialeMensuelle*12)}/an</div>
          </div>
        </div>
      </div>

      {/* ── Décomposition ── */}
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
        <div className="font-semibold text-sm text-gray-800 mb-3">📊 Décomposition du seuil</div>
        <div className="space-y-2 text-sm">
          {[
            {label:'Charges fixes',     val:r.chargesFixesMensuelles,  unit:'HT/mois', color:'#1A3C6E'},
            {label:'Masse salariale',   val:r.masseSalarialeMensuelle, unit:'HT/mois', color:'#2563EB'},
            {label:'Objectif bénéfice', val:p.objectifBenefice,        unit:'HT/mois', color:'#64748B'},
          ].map(row=>(
            <div key={row.label} className="flex justify-between py-1 border-b border-gray-50">
              <span className="text-gray-600">{row.label}</span>
              <span className="font-semibold font-mono" style={{color:row.color}}>{formatEuros(row.val)} <span className="text-xs font-normal text-gray-400">{row.unit}</span></span>
            </div>
          ))}
          <div className="flex justify-between py-1 border-b border-gray-50">
            <div><span className="text-gray-600">Produits tech. utilisés </span>
              <span className="text-xs px-1.5 py-0.5 rounded ml-1 font-medium" style={{background:'#FEE2E2',color:'#991B1B'}}>↑ +{p.pctProduitsTechUtilises}%</span></div>
            <span className="font-semibold font-mono" style={{color:'#C62828'}}>+{formatEuros(r.coutProduitsTechMensuel)}</span>
          </div>
          {/* Ligne TVA */}
          <div className="flex justify-between py-1 border-b border-gray-50">
            <div><span className="text-gray-600">= Seuil HT</span></div>
            <span className="font-semibold font-mono text-gray-700">{formatEuros(r.caMinimumHT)}</span>
          </div>
          <div className="flex justify-between py-2 font-bold">
            <div>
              <span className="text-gray-800">+ TVA {p.tauxTVA}% → Seuil TTC</span>
              <div className="text-xs font-normal text-gray-400">ce que le salon encaisse réellement</div>
            </div>
            <span className="text-lg font-mono" style={{color:'#1A3C6E'}}>{formatEuros(r.caMinimumTTC)}</span>
          </div>
          <div className="flex justify-between py-1 text-xs text-gray-500 border-t border-gray-100 pt-2">
            <span>{p.modeGerant === 'passif' ? '⬆ Seuil équipe (mode actif)' : '⬇ Seuil équipe sans gérant (passif)'}</span>
            <span className="font-semibold">{formatEuros(r.caMinimumSansGerantTTC)}/mois TTC</span>
          </div>
        </div>
      </div>

      {/* ── Seuil à la minute et à l'heure ── */}
      {hasH && (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 pt-4 pb-2">
            <div className="font-semibold text-sm text-gray-800">⏱ Seuil de rentabilité</div>
            <p className="text-xs text-gray-400 mt-0.5">{r.heuresProductivesTotales.toFixed(0)} h productives/mois · {collaborateurs.length} collab.</p>
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100">
            <div className="p-4 text-center">
              <div className="text-xs text-gray-500 mb-1 font-medium">Seuil / minute (TTC)</div>
              <div className="text-3xl font-black font-mono" style={{color:'#1A3C6E'}}>{formatEurosCents(r.seuilMinuteTTC)}</div>
              <div className="text-xs text-gray-400 mt-0.5">par minute productive</div>
            </div>
            <div className="p-4 text-center">
              <div className="text-xs text-gray-500 mb-1 font-medium">Seuil / heure (TTC)</div>
              <div className="text-3xl font-black font-mono" style={{color:'#1A3C6E'}}>{formatEuros(r.seuilHoraireTTC)}</div>
              <div className="text-xs text-gray-400 mt-0.5">par heure productive</div>
            </div>
          </div>
          <div className="border-t border-gray-100 px-4 py-3" style={{background:r.ecartPct>20?'#FFF5F5':'#F8FAFC'}}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">⚡ Coût-minute réel (TTC)</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{background:`${ecartColor}15`,color:ecartColor}}>+{r.ecartPct.toFixed(1)}%</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Agenda à {p.tauxRemplissage}% · si rempli à 100% : {formatEurosCents(r.coutMinuteTheoriqueTTC)}</p>
              </div>
              <div className="text-right">
                <div className="text-xl font-black font-mono" style={{color:ecartColor}}>{formatEurosCents(r.coutMinuteReelTTC)}</div>
                <div className="text-xs text-gray-400">TTC / minute</div>
              </div>
            </div>
            {r.ecartPct>10 && (
              <div className="mt-2 text-xs p-2 rounded-lg" style={{background:'#FFF9E6',color:'#92400E'}}>
                ⚠ Référence de tarification : le coût-minute <strong>réel</strong>. Augmenter le taux de remplissage réduit ce coût.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Alertes ── */}
      {p.objectifBenefice===0 && (
        <div className="rounded-xl p-3 flex items-start gap-2 text-sm" style={{background:'#FFF5F5',border:'1px solid #FEE2E2',color:'#991B1B'}}>
          <span>🔴</span><div><strong>Objectif bénéfice à 0 €</strong><div className="text-xs mt-0.5 opacity-80">Définissez un objectif pour intégrer votre rentabilité cible.</div></div>
        </div>
      )}

      {/* ── Performance collabs ── */}
      {hasCA && (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
          <div className="text-sm font-semibold text-gray-800 mb-3">👥 Performance équipe</div>
          <div className="space-y-3">
            {collaborateurs.filter(c=>(c.caReel??0)>0).map(c=>{
              const perf=perfCollab(c,r.caMinimumTTC,r.heuresProductivesTotales)
              const col=perf>=100?'#2E7D32':perf>=75?'#E65100':'#C62828'
              return (<div key={c.id}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700">{c.prenom}</span>
                  <span className="font-bold" style={{color:col}}>{perf.toFixed(0)}% {perf>=100?'✅':perf<75?'⚠️':''}</span>
                </div>
                <div className="w-full h-2 rounded-full" style={{background:'#E2E8F0'}}>
                  <div className="h-2 rounded-full transition-all duration-500" style={{width:`${Math.min(perf,100)}%`,background:col}}/>
                </div>
              </div>)
            })}
          </div>
        </div>
      )}

      {/* ── Graphiques ── */}
      {(r.chargesFixesMensuelles>0||r.masseSalarialeMensuelle>0) && (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
          <button onClick={()=>setShowCharts(!showCharts)} className="w-full flex items-center justify-between text-sm font-semibold text-gray-800">
            <span>📊 Graphiques</span><span className="text-gray-400 text-xs">{showCharts?'▲':'▼'}</span>
          </button>
          {showCharts && (
            <div className="mt-4 space-y-4">
              {pieData.length>0 && (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="45%" height={110}>
                    <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={28} outerRadius={50} dataKey="value" paddingAngle={2}>
                      {pieData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                    </Pie><Tooltip formatter={(v:number)=>formatEuros(v)}/></PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {pieData.map((d,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{background:d.color}}/><span className="text-gray-600">{d.name}</span></div>
                        <span className="font-semibold font-mono">{formatEuros(d.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {barData.length>0 && (
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart data={barData} margin={{top:0,right:0,left:-25,bottom:0}}>
                    <XAxis dataKey="name" tick={{fontSize:9}}/><YAxis tick={{fontSize:9}}/>
                    <Tooltip formatter={(v:number,_:string,pr:{payload?:{fullName?:string}})=>[formatEuros(v),pr.payload?.fullName??'']}/>
                    <Bar dataKey="value" radius={[3,3,0,0]}>{barData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Couverture ── */}
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
        <div className="text-sm font-semibold text-gray-800 mb-3">📈 Couverture du seuil</div>
        <div className="relative">
          <input type="number" min={0} value={caReel||''} onChange={e=>onCaReelChange(parseFloat(e.target.value)||0)}
            placeholder="CA réel du mois (€ TTC)"
            className="w-full pl-3 pr-16 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"/>
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€ TTC</span>
        </div>
        {caReel>0&&r.caMinimumTTC>0&&(
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Couverture</span>
              <span className="font-bold" style={{color:couverture>=100?'#2E7D32':couverture>=80?'#E65100':'#C62828'}}>{couverture.toFixed(0)}%</span>
            </div>
            <div className="w-full h-3 rounded-full" style={{background:'#E2E8F0'}}>
              <div className="h-3 rounded-full transition-all duration-500"
                style={{width:`${Math.min(couverture,100)}%`,background:couverture>=100?'#2E7D32':couverture>=80?'#E65100':'#C62828'}}/>
            </div>
            <div className="text-xs mt-2 text-gray-500">
              {couverture>=100 ? `✅ Seuil atteint ! Bénéfice estimé : ${formatEuros(caReel-r.caMinimumTTC)}`
                : `Il manque ${formatEuros(r.caMinimumTTC-caReel)} pour atteindre le seuil`}
            </div>
          </div>
        )}
      </div>

      {/* ── CTA ── */}
      <div className="rounded-2xl border-2 bg-white p-4" style={{borderColor:'#1A3C6E'}}>
        <div className="text-sm font-semibold text-gray-800 mb-2">💼 Version complète</div>
        <div className="grid grid-cols-2 gap-1 mb-3">
          {['✓ Charte tarifaire','✓ Suivi mensuel','✓ Rapports multi-mois','✓ Module produits','✓ Badge / temps','✓ Sauvegarde cloud'].map(f=>(
            <p key={f} className="text-xs font-medium" style={{color:'#1A3C6E'}}>{f}</p>
          ))}
        </div>
        <a href="https://wa.me/33604417682?text=Bonjour%20Loris%2C%20je%20souhaite%20acc%C3%A9der%20%C3%A0%20la%20version%20compl%C3%A8te%20de%20l%27outil%20Pilotage%20Salon."
          target="_blank" rel="noopener noreferrer"
          className="block w-full py-2.5 rounded-xl font-bold text-white text-sm text-center hover:opacity-90" style={{background:'#25D366'}}>
          💬 Demander l'accès complet sur WhatsApp →
        </a>
      </div>
    </div>
  )
}
